import React, { useState, useEffect, useMemo } from 'react';
import { 
    Users, Building2, CreditCard, ArrowUpCircle, TrendingUp, 
    ChevronRight, LayoutGrid, Clock, CheckCircle2, AlertCircle,
    ArrowUpRight, Target, Activity, DollarSign
} from 'lucide-react';
import { companyService } from '../../../../services/companyService';
import { countPendingUpgradeRequests } from '../../../../services/api/upgradeRequestApi';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';

const PLAN_PRICES = {
    BASIC: 0,
    STANDARD: 299,
    PROFESSIONAL: 599,
    ENTERPRISE: 1999 // Estimated base
};

const PLAN_GRADIENTS = {
    BASIC: 'from-slate-400 to-slate-600',
    STANDARD: 'from-blue-400 to-blue-600',
    PROFESSIONAL: 'from-purple-400 to-purple-600',
    ENTERPRISE: 'from-amber-400 to-amber-600'
};

const KpiCard = ({ title, value, icon: Icon, gradient, subtitle, trend }) => (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 transition-all hover:shadow-md">
        <div className="flex items-start justify-between mb-4">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white shadow-lg shadow-gray-200`}>
                <Icon size={24} />
            </div>
            {trend && (
                <div className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg text-xs font-bold">
                    <ArrowUpRight size={14} /> {trend}%
                </div>
            )}
        </div>
        <div>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">{title}</p>
            <h3 className="text-2xl font-black text-gray-900">{value}</h3>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
    </div>
);

const SystemAdminDashboard = ({ onNavigate }) => {
    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [companiesData, count] = await Promise.all([
                companyService.getCompanies(),
                countPendingUpgradeRequests()
            ]);
            const companiesList = Array.isArray(companiesData) 
                ? companiesData 
                : (companiesData?.content || companiesData?.data || []);
            setCompanies(companiesList);
            setPendingRequestsCount(count || 0);
        } catch (error) {
            console.error('Failed to load system admin dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const stats = useMemo(() => {
        const active = companies.filter(c => (c.status || '').toUpperCase() === 'ACTIVE');
        const planGroups = active.reduce((acc, c) => {
            const p = (c.subscriptionPlan || 'BASIC').toUpperCase();
            acc[p] = (acc[p] || 0) + 1;
            return acc;
        }, {});

        const mrr = Object.entries(planGroups).reduce((total, [plan, count]) => {
            return total + (PLAN_PRICES[plan] || 0) * count;
        }, 0);

        // Map newest first
        const recentSignups = [...companies]
            .sort((a, b) => (b.id && a.id) ? b.id - a.id : 0) // Fallback sorting
            .slice(0, 5);

        return {
            totalTenants: companies.length,
            activeTenants: active.length,
            mrr,
            planGroups,
            recentSignups
        };
    }, [companies]);

    if (loading) return <div className="p-8 flex justify-center"><LoadingSpinner size="lg" message="Calculating platform metrics..." /></div>;

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50/30 p-6 md:p-8 space-y-8">
            {/* Welcome Header */}
            <div>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">SaaS Command Center</h1>
                <p className="text-sm text-gray-500 mt-1">Real-time overview of platform health, growth, and revenue.</p>
            </div>

            {/* KPI Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <KpiCard 
                    title="Active Tenants" 
                    value={stats.activeTenants} 
                    icon={Building2} 
                    gradient="from-indigo-600 to-blue-700"
                    subtitle={`${stats.totalTenants - stats.activeTenants} inactive / pending`}
                    trend={12}
                />
                <KpiCard 
                    title="Monthly Revenue (MRR)" 
                    value={`AED ${stats.mrr.toLocaleString()}`} 
                    icon={DollarSign} 
                    gradient="from-emerald-500 to-teal-600"
                    subtitle="Estimated from active plans"
                    trend={8}
                />
                <KpiCard 
                    title="Plan Upgrades" 
                    value={pendingRequestsCount} 
                    icon={ArrowUpCircle} 
                    gradient="from-orange-400 to-rose-500"
                    subtitle={pendingRequestsCount > 0 ? 'Requires immediate action' : 'All requests processed'}
                />
                <KpiCard 
                    title="System Health" 
                    value="99.9%" 
                    icon={Activity} 
                    gradient="from-cyan-500 to-blue-500"
                    subtitle="Node cluster status: OK"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Plan Distribution */}
                <div className="lg:col-span-1 bg-white border border-gray-200 rounded-3xl p-6 shadow-sm">
                    <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-6 flex items-center justify-between">
                        <span>Revenue by Plan Tier</span>
                        <Target size={14} className="text-gray-400" />
                    </h2>
                    
                    <div className="space-y-6">
                        {Object.entries(PLAN_GRADIENTS).map(([plan, gradient]) => {
                            const count = stats.planGroups[plan] || 0;
                            const share = stats.activeTenants > 0 ? (count / stats.activeTenants) * 100 : 0;
                            const revenue = count * (PLAN_PRICES[plan] || 0);
                            
                            return (
                                <div key={plan}>
                                    <div className="flex justify-between items-end mb-2">
                                        <div>
                                            <p className="text-xs font-bold text-gray-900">{plan}</p>
                                            <p className="text-[10px] text-gray-400">{count} active tenants</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs font-bold text-gray-900">AED {revenue.toLocaleString()}</p>
                                            <p className="text-[10px] text-gray-400">{share.toFixed(0)}% share</p>
                                        </div>
                                    </div>
                                    <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full bg-gradient-to-r ${gradient}`} 
                                            style={{ width: `${share}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Recent Signups */}
                <div className="lg:col-span-2 bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
                    <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Newest Companies</h2>
                        <button 
                            onClick={() => onNavigate('companies')}
                            className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                        >
                            View All <ChevronRight size={14} />
                        </button>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50/50">
                                <tr className="text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                    <th className="px-6 py-4">Company</th>
                                    <th className="px-6 py-4">Plan</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4 text-right">Details</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {stats.recentSignups.map(c => (
                                    <tr key={c.id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold text-xs">
                                                    {c.name?.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">{c.name}</p>
                                                    <p className="text-[10px] text-gray-400">{c.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-[10px] font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded uppercase border border-gray-200">
                                                {c.subscriptionPlan}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-xs">
                                            <div className="flex items-center gap-1.5">
                                                <div className={`w-1.5 h-1.5 rounded-full ${c.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                                <span className="font-medium text-gray-600 capitalize">{c.status}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button 
                                                onClick={() => onNavigate('company-detail', c)}
                                                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all"
                                            >
                                                <ChevronRight size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystemAdminDashboard;
